import React from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import {
  cta2FontWeight,
  screenMdMin,
  screenSmMax,
  screenXsMax,
  spacer16,
  spacer24,
  spacer32,
  spacer48,
  spacer8,
} from '@spotify-internal/encore-foundation';
import { Type } from '@spotify-internal/encore-web';
import {
  Link,
  ButtonTertiaryAsLink,
  withAnimationContext,
  DELAYS,
  fadeInAnimation,
} from '../../ui';
import { ModuleType } from '..';
import { fixHref, formatText, getBurst, transformScaleUp } from '../../utils';
import androidIcon from '../../Layout/images/footer-android.svg';
import iosIcon from '../../Layout/images/footer-ios.svg';

const TRACK_CAT = 'module-pageBreaker';

type CTAButtonType = {
  ctaText?: string;
  ctaLink?: string;
  title?: string;
};

const PageBreaker = styled.div<any>`
  display: flex;
  align-items: center;
  min-height: 236px;

  ${(props: { showBurst: boolean; theme: any }) =>
    props.showBurst ? getBurst(props.theme.box.text) : ''};

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    justify-content: center;
    min-height: auto;
    background-image: none;
  }
`;

const Left = styled.div`
  width: 100%;
  padding-top: ${spacer16};
  padding-bottom: ${spacer32};
  @media (max-width: ${screenSmMax}) {
    text-align: left;
  }
`;

const Right = styled.div`
  width: 100%;
  padding-top: ${spacer32};
  padding-bottom: ${spacer32};
  @media (max-width: ${screenSmMax}) {
    padding-top: 0px;
    padding-bottom: ${spacer16};
    align-items: center;
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: 'heading1',
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    font-weight: ${cta2FontWeight};
    padding-bottom: 0;
  `,
);

const SubtextContainer = withAnimationContext(
  styled.div.attrs({
    variant: 'body1',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-top: ${spacer8};
    padding-bottom: ${spacer8};
    @media (max-width: ${screenSmMax}) {
      padding-bottom: 0;
    }
  `,
);

const StyledImage = styled.img`
  @media (min-width: ${screenMdMin}) {
    padding-right: ${spacer24};
    padding-left: ${spacer24};
  }
  @media (max-width: ${screenSmMax}) {
    padding-right: ${spacer8};
  }
`;

const CTAContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: ${spacer48};

  @media (max-width: ${screenSmMax}) {
    justify-content: initial;
  }

  @media (max-width: ${screenXsMax}) {
    padding-top: 0px;
    padding-bottom: ${spacer24};
    padding-right: 0;
    justify-content: center;
  }
`;

const CTABtn = styled(ButtonTertiaryAsLink)`
  && {
    background-color: ${({ theme }) => theme.cta.background};
    color: ${({ theme }) => theme.cta.text};
  }

  &:hover:not(:focus):not(:disabled) {
    color: ${({ theme }) => theme.cta.text};
    text-decoration: none;
    ${transformScaleUp};
  }

  &:focus:not(:disabled) {
    color: ${({ theme }) => theme.cta.text};
    outline: thin dotted black;
  }

  @media (max-width: ${screenSmMax}) {
    &:hover:not(:focus):not(:disabled) {
      transform: none;
    }
  }
`;

const AppStores: React.FC = () => {
  return (
    <>
      <Link
        external
        href="https://itunes.apple.com/us/app/spotify-for-artists/id1222021797"
        track={[TRACK_CAT, `CTA App Store Link - iOS`]}
      >
        <StyledImage src={iosIcon} alt="Download banner for iOS" />
      </Link>
      <Link
        external
        href="https://play.google.com/store/apps/details?id=com.spotify.s4a"
        track={[TRACK_CAT, `CTA App Store Link - Android`]}
      >
        <img src={androidIcon} alt="Download banner for Android" />
      </Link>
    </>
  );
};

const AppStoresContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: ${screenSmMax}) {
    padding-top: ${spacer16};
    padding-bottom: ${spacer16};
    padding-left: ${spacer16};
    justify-content: flex-start;
  }
  a:focus img {
    ${transformScaleUp};
  }
`;

const getPageBreakerVersion = (
  moduleVersion: string,
  ctaButton: CTAButtonType,
) => {
  switch (moduleVersion) {
    case 'Call to Action':
      if (
        get(ctaButton, 'ctaText.length') > 0 &&
        get(ctaButton, 'ctaLink.length') > 0
      ) {
        return (
          <CTAContainer>
            <CTABtn
              href={fixHref(ctaButton.ctaLink)}
              track={[TRACK_CAT, `CTA Link - ${ctaButton.title}`]}
            >
              {ctaButton.ctaText}
            </CTABtn>
          </CTAContainer>
        );
      }
      return <></>;
    case 'App Stores':
      return (
        <AppStoresContainer>
          <AppStores />
        </AppStoresContainer>
      );
    default:
      return <></>;
  }
};

export type PageBreakerModuleType = ModuleType & {
  headline?: string;
  subtext?: string;
  ctaButton?: CTAButtonType;
  moduleVersion?: string;
  showBurst?: boolean;
};

export const PageBreakerModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  PageBreakerModuleType
>(
  (
    {
      headline = '',
      subtext = '',
      ctaButton = {
        ctaText: '',
        ctaLink: '',
        title: '',
      } as CTAButtonType,
      moduleVersion = 'Call to Action',
      showBurst = false,
    },
    ref,
  ) => {
    return (
      <PageBreaker
        ref={ref}
        hasSubtext={Boolean(subtext)}
        showBurst={showBurst}
      >
        <Left>
          {headline && (
            <Headline
              dangerouslySetInnerHTML={{ __html: formatText(headline) }}
            />
          )}
          {subtext && (
            <SubtextContainer
              dangerouslySetInnerHTML={{ __html: formatText(subtext) }}
            />
          )}
        </Left>
        <Right>{getPageBreakerVersion(moduleVersion, ctaButton)}</Right>
      </PageBreaker>
    );
  },
);
